import { useSeo } from "@/lib/seo";
import { useMarginCalculator } from './useMarginCalculator';
import MarginCalculator from './components/MarginCalculator';
import WhySection from './components/WhySection';
import FormulaSection from './components/FormulaSection';
import { MistakesSection } from './components/InfoSections';
import Reveal from '@/pages/lot-size/components/Reveal';
import AppHeader from '@/pages/home/components/AppHeader';
import Footer from '@/pages/home/components/Footer';
import AppEnding from '@/pages/home/components/AppEnding';

export default function MarginPage() {
  useSeo({
    /* @replace:margin-title */
    title: 'Máy tính Ký quỹ Forex Chính xác | Kiểm soát Đòn bẩy & Vốn - ForexMatrix',
    /* @replace:margin-description */
    description: 'Sử dụng Máy tính Ký quỹ (Margin Calculator) của ForexMatrix để tính toán chính xác số tiền ký quỹ cần thiết cho mọi lệnh giao dịch ngoại hối, vàng và tài sản khác. Công cụ đắc lực giúp bạn kiểm soát đòn bẩy và phòng tránh Margin Call.',
    /* @replace:margin-keywords */
    keywords: 'máy tính ký quỹ forex, margin calculator forex, tính tiền ký quỹ, quản lý đòn bẩy, tính ký quỹ vàng, công cụ trade forex, forex matrix',
    path: '/may-tinh-ky-quy',
  });
  const calc = useMarginCalculator();

  return (
    <div className="relative min-h-screen bg-[#F8FAFC] text-slate-900">
      <AppHeader />
      <main className="pb-8">
        <div className="mx-auto max-w-6xl px-4 pt-28 pb-16 md:px-8 md:pt-32 md:pb-20">
          {/* Header */}
          <Reveal>
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center">
              <div className="lg:col-span-7">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700 ring-1 ring-violet-100">
                  <i className="ri-calculator-line" />
                  Công cụ tính toán
                </span>
                <h1 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
                  Máy tính Ký quỹ Forex
                </h1>
                <p className="mt-3 text-base leading-relaxed text-slate-600 md:text-lg">
                  Tính toán số tiền ký quỹ cần thiết dựa trên khối lượng lệnh, đòn bẩy và giá hiện tại.
                </p>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">
                  Công cụ giúp bạn biết chính xác số tiền bị giữ cho một vị thế trước khi vào lệnh.
                </p>
                <ul className="mt-6 space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-lg bg-violet-100 text-violet-600">
                      <i className="ri-flashlight-line text-sm" />
                    </span>
                    <p className="text-sm leading-relaxed text-slate-600">
                      <strong className="font-semibold text-slate-800">Kết quả tức thì</strong> — nhập số liệu là có
                      ngay số tiền ký quỹ cần thiết cho từng cặp tiền.
                    </p>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-lg bg-violet-100 text-violet-600">
                      <i className="ri-funds-line text-sm" />
                    </span>
                    <p className="text-sm leading-relaxed text-slate-600">
                      <strong className="font-semibold text-slate-800">So sánh đòn bẩy</strong> — thử nhiều mức đòn bẩy
                      để thấy rõ mức ký quỹ thay đổi ra sao.
                    </p>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-lg bg-violet-100 text-violet-600">
                      <i className="ri-eye-line text-sm" />
                    </span>
                    <p className="text-sm leading-relaxed text-slate-600">
                      <strong className="font-semibold text-slate-800">Trực quan dễ hiểu</strong> — giao diện gọn gàng,
                      phù hợp cả người mới bắt đầu lẫn trader có kinh nghiệm.
                    </p>
                  </li>
                </ul>
              </div>

              {/* What is margin calculator */}
              <div className="lg:col-span-5">
                <div className="rounded-3xl border border-violet-100 bg-gradient-to-br from-violet-50/70 to-white p-6 md:p-7">
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-600 text-white">
                      <i className="ri-question-mark text-xl" />
                    </span>
                    <h2 className="font-display text-lg font-bold text-slate-900">Ký quỹ là gì?</h2>
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-slate-600">
                    Máy tính Ký quỹ cho biết chính xác số tiền sẽ bị "đóng băng" khi bạn mở một lệnh giao dịch có đòn
                    bẩy.
                  </p>
                  <ul className="mt-5 space-y-3">
                    <li className="flex items-start gap-2.5">
                      <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-violet-100 text-violet-600">
                        <i className="ri-arrow-go-back-line text-xs" />
                      </span>
                      <p className="text-xs leading-relaxed text-slate-500">
                        <strong className="font-semibold text-slate-700">Không phải phí</strong> — khoản tiền tạm giữ,
                        sẽ được hoàn lại khi bạn đóng lệnh.
                      </p>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-violet-100 text-violet-600">
                        <i className="ri-stack-line text-xs" />
                      </span>
                      <p className="text-xs leading-relaxed text-slate-500">
                        <strong className="font-semibold text-slate-700">Tính từ 3 yếu tố</strong> — khối lượng lệnh,
                        đòn bẩy và giá hiện tại.
                      </p>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-violet-100 text-violet-600">
                        <i className="ri-shield-check-line text-xs" />
                      </span>
                      <p className="text-xs leading-relaxed text-slate-500">
                        <strong className="font-semibold text-slate-700">Tránh bị đóng lệnh</strong> — biết trước số tiền
                        cần thiết để chừa đủ biên an toàn.
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Core calculator */}
          <div className="mt-10">
            <Reveal delay={80}>
              <MarginCalculator calc={calc} />
            </Reveal>
          </div>

          {/* Content modules */}
          <div className="mt-16 space-y-16">
            <Reveal>
              <WhySection />
            </Reveal>
            <Reveal>
              <FormulaSection />
            </Reveal>
            <Reveal>
              <MistakesSection />
            </Reveal>
          </div>
        </div>
      </main>
      <Footer />
      <AppEnding />
    </div>
  );
}